import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ToastProvider } from '../../contexts/ToastContext';
import Logo from '../Logo';

const BrandLink: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <Link
    to="/admin"
    onClick={onClick}
    className="flex items-center gap-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-mint/50 rounded-md"
    aria-label="SeaDent Dental Practice Management — Dashboard"
  >
    <Logo
      className="transition-transform duration-300 ease-out group-hover:scale-[1.03] drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
      color="#93cbb6"
    />
    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] rounded-sm bg-mint/15 text-mint border border-mint/30 backdrop-blur-sm">
      Beta
    </span>
  </Link>
);

interface NavItem {
  name: string;
  href: string;
  icon: string;
  current: boolean;
}

const NavLink: React.FC<{ item: NavItem; onClick?: () => void }> = ({ item, onClick }) => (
  <Link
    to={item.href}
    onClick={onClick}
    aria-current={item.current ? 'page' : undefined}
    className={`group relative flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
      item.current
        ? 'bg-mint/15 text-navy-admin shadow-sm'
        : 'text-gray-600 hover:bg-gray-50 hover:text-navy-admin'
    }`}
  >
    <span
      aria-hidden
      className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full bg-mint transition-all duration-200 ${
        item.current ? 'h-6 opacity-100' : 'h-0 opacity-0 group-hover:h-4 group-hover:opacity-60'
      }`}
    />
    <span
      className={`mr-3 text-base transition-transform duration-200 ${
        item.current ? 'scale-110' : 'group-hover:scale-105'
      }`}
    >
      {item.icon}
    </span>
    {item.name}
  </Link>
);

const LogoutIcon: React.FC = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navigation = [
    { 
      name: 'Dashboard', 
      href: '/admin', 
      icon: '📊',
      current: location.pathname === '/admin'
    },
    { 
      name: 'Calendar', 
      href: '/admin/calendar', 
      icon: '📅',
      current: location.pathname === '/admin/calendar'
    },
    { 
      name: 'Appointments', 
      href: '/admin/appointments', 
      icon: '📋',
      current: location.pathname === '/admin/appointments'
    },
    { 
      name: 'Providers', 
      href: '/admin/providers', 
      icon: '👨‍⚕️',
      current: location.pathname === '/admin/providers'
    },
    { 
      name: 'Procedures', 
      href: '/admin/procedures', 
      icon: '🦷',
      current: location.pathname === '/admin/procedures'
    },
    { 
      name: 'Operatories', 
      href: '/admin/operatories', 
      icon: '🏥',
      current: location.pathname === '/admin/operatories'
    },
    {
      name: 'Reports',
      href: '/admin/reports',
      icon: '📈',
      current: location.pathname === '/admin/reports'
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: '⚙️',
      current: location.pathname === '/admin/settings'
    },
    {
      name: 'Help',
      href: '/admin/help',
      icon: '❓',
      current: location.pathname === '/admin/help'
    }
  ];

  return (
    <ToastProvider>
      <div className="bg-gray-100 min-h-screen">
        {/* Mobile sidebar */}
        <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)}></div>
          <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
            <div className="relative flex items-center justify-between h-16 px-4 bg-gradient-to-br from-navy-admin via-navy-admin to-navy-dark shadow-md after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-mint/40 after:to-transparent">
              <BrandLink onClick={() => setSidebarOpen(false)} />
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-white hover:text-gray-300"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex-1 px-3 py-4">
              <ul className="space-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <NavLink item={item} onClick={() => setSidebarOpen(false)} />
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-64 lg:flex-col">
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
            <div className="relative flex items-center h-16 px-4 bg-gradient-to-br from-navy-admin via-navy-admin to-navy-dark shadow-md after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-mint/40 after:to-transparent">
              <BrandLink />
            </div>
            <nav className="flex-1 px-3 py-4">
              <ul className="space-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <NavLink item={item} />
                  </li>
                ))}
              </ul>
            </nav>
            <div className="px-4 py-3 border-t border-gray-100">
              <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-semibold">SeaDent · v1.0</p>
            </div>
          </div>
        </div>

        <div className="lg:pl-64">
          {/* Top header */}
          <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
            <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
              <div className="flex items-center">
                <button
                  type="button"
                  className="lg:hidden -ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-navy-admin"
                  onClick={() => setSidebarOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                
                <div className="ml-4 lg:ml-0 flex flex-col">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-mint/90 leading-none whitespace-nowrap">
                    Dental Practice Management
                  </span>
                  <h1 className="text-lg font-semibold text-gray-900 leading-tight mt-0.5">
                    {navigation.find(item => item.current)?.name || 'Dashboard'}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="hidden md:flex flex-col items-end leading-tight">
                  <span className="text-sm font-semibold text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </span>
                  <span className="text-xs text-gray-500">{user?.email}</span>
                </div>
                <div
                  className="h-9 w-9 rounded-full bg-gradient-to-br from-navy-admin to-navy-dark flex items-center justify-center ring-2 ring-mint/40 ring-offset-2 ring-offset-white shadow-sm"
                  aria-hidden
                >
                  <span className="text-sm font-semibold text-white">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  title="Logout"
                  aria-label="Logout"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  <LogoutIcon />
                </button>
              </div>
            </div>
          </div>

          {/* Main content */}
          <main className="py-6">
            <div className="px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </ToastProvider>
  );
};

export default AdminLayout; 